import { cssLengthToPx } from '../../../utils/proposalUnits';

/**
 * Une seule question : combien de slides à l'écran ?
 *
 * - par défaut, N slides par vue (1..6), chacune prend 100/N de la largeur, et
 *   le défilement est pas-à-pas — il s'arrête sur chaque slide (plugin Autoplay
 *   d'Embla, dont le `delay` est ce temps d'arrêt) ;
 * - `maxSlidesVisible` : chaque slide prend la largeur de son contenu, il en
 *   tient autant que la largeur le permet et le ruban défile sans jamais
 *   s'arrêter (plugin AutoScroll). C'est le bandeau de logos, où « combien d'un
 *   coup » et « combien de temps sur chacun » n'ont pas de réponse chiffrée.
 */
export const CAROUSEL_VISIBLE_MAX = 6;

/**
 * Plancher de largeur d'une slide auto (CarouselBlock.module.css). Sans lui une
 * slide sans média se réduit à la largeur de son texte, jusqu'à devenir un
 * filet illisible — et en édition, impossible à cliquer.
 */
export const CAROUSEL_AUTO_MIN_WIDTH_PX = 200;

/** Nombre de slides par vue, borné 1..6. */
export function carouselVisibleCount(slidesVisible) {
  return Math.max(1, Math.min(CAROUSEL_VISIBLE_MAX, Number(slidesVisible) || 1));
}

/** Hauteur de ruban vertical par défaut, en px. */
export const CAROUSEL_STRIP_HEIGHT_DEFAULT_PX = 500;
/** Hauteur de cadre par défaut, en px. */
export const CAROUSEL_HEIGHT_DEFAULT_PX = 400;

/**
 * Taille d'une slide, au sens de Mantine : `flex: 0 0 var(--carousel-slide-size)`
 * — donc une LARGEUR à l'horizontale, une HAUTEUR à la verticale.
 *
 * - ruban continu horizontal : `auto`, la slide se dimensionne sur son contenu
 *   (plancher CAROUSEL_AUTO_MIN_WIDTH_PX sans lequel une slide sans média
 *   s'effondre) ;
 * - ruban continu vertical : `auto` s'effondrerait pour de bon — la hauteur du
 *   contenu d'une slide n'est pas intrinsèque (le média est en `flex: 1 1 auto`).
 *   D'où une hauteur explicite : celle du champ « Hauteur des slides » ;
 * - nombre fixe : une fraction égale du cadre, dans l'axe courant.
 */
export function carouselSlideSize({
  maxSlidesVisible, slidesVisible, vertical = false, slideHeightPx = 0, slideGapPx = 0,
}) {
  // À la verticale, une slide vaut la hauteur demandée, dans les deux modes.
  // Mantine range la gouttière DANS la slide (`includeGapInSize`, vrai par
  // défaut : padding-bottom à la verticale) : sans l'ajouter ici, elle serait
  // prise sur la hauteur demandée — 96 px de slide moins 48 px de gouttière
  // moins le padding du contenu, et le média tombait à zéro.
  if (vertical) {
    return `${(slideHeightPx || CAROUSEL_HEIGHT_DEFAULT_PX) + Math.max(slideGapPx, 0)}px`;
  }
  if (maxSlidesVisible) return 'auto';
  const pct = 100 / carouselVisibleCount(slidesVisible);
  return Number.isInteger(pct) ? `${pct}%` : `${pct.toFixed(4)}%`;
}

/**
 * Hauteur du cadre (`--carousel-height`), qui n'est un réglage que dans un cas.
 *
 * - horizontal : le cadre EST la hauteur d'une slide ;
 * - vertical, nombre fixe : il se DÉDUIT — N slides de la hauteur demandée,
 *   séparées de N−1 gouttières. Rien à régler : demander en plus une hauteur de
 *   cadre reviendrait à donner deux réponses à la même question ;
 * - vertical, ruban continu : là seulement, il se règle. Les slides défilent
 *   sans fin, leur nombre ne dit plus quelle portion on regarde.
 */
export function carouselViewportHeightPx({
  vertical = false,
  maxSlidesVisible = false,
  heightPx = 0,
  stripHeightPx = 0,
  slidesVisible = 1,
  slideGapPx = 0,
}) {
  if (!vertical) return heightPx || CAROUSEL_HEIGHT_DEFAULT_PX;
  if (maxSlidesVisible) return stripHeightPx || CAROUSEL_STRIP_HEIGHT_DEFAULT_PX;
  const count = carouselVisibleCount(slidesVisible);
  const slide = heightPx || CAROUSEL_HEIGHT_DEFAULT_PX;
  return count * slide + (count - 1) * Math.max(slideGapPx, 0);
}

/**
 * Nombre de fois qu'il faut répéter le jeu de slides pour que le ruban puisse
 * boucler. Embla désactive la boucle quand les slides ne débordent pas du
 * cadre : il n'y a alors rien à faire défiler, et le défilement continu
 * s'arrête sur la dernière slide. Six logos qui remplissent tout juste la
 * largeur suffisent à déclencher le cas.
 *
 * On vise le double de la largeur visible : la boucle a besoin de déborder, pas
 * seulement d'atteindre le bord.
 */
export function carouselLoopRepeat({ viewportWidth, setWidth }) {
  if (!(viewportWidth > 0) || !(setWidth > 0)) return 1;
  return Math.max(1, Math.ceil((viewportWidth * 2) / setWidth));
}

/**
 * Style du média de contenu.
 * - largeur donnée : elle prime, dans les deux modes ;
 * - sinon en mode auto, `aspect-ratio` donne au média une largeur intrinsèque
 *   à hauteur imposée, et c'est elle que la slide adopte ;
 * - sinon, le média occupe toute la largeur de la slide (défaut CSS).
 */
export function carouselContentMediaStyle({ contentMediaWidth, maxSlidesVisible, ratio }) {
  const width = Number(contentMediaWidth);
  if (Number.isFinite(width) && width > 0) return { width: `${width}px` };
  if (maxSlidesVisible) return { aspectRatio: ratio };
  return undefined;
}

export const CAROUSEL_EDGE_FADE_DEFAULT_PX = 48;

/**
 * Largeur du fondu sur les bords gauche et droit du ruban, en px — `null`
 * quand il n'y a pas de fondu à appliquer.
 *
 * Le fondu lui-même est un `mask-image` (CarouselBlock.module.css) et non un
 * dégradé vers une couleur : le contenu s'efface vers le transparent quel que
 * soit le fond du Bloc derrière, là où un dégradé aurait dû connaître ce fond.
 */
export function carouselEdgeFadeWidthPx({ maxSlidesVisible, edgeFade, edgeFadeWidth }) {
  if (!maxSlidesVisible || !edgeFade) return null;
  const px = cssLengthToPx(edgeFadeWidth);
  return px > 0 ? px : CAROUSEL_EDGE_FADE_DEFAULT_PX;
}

/** Espacement entre slides : longueur CSS (champ px↔rem) → px pour Mantine. */
export function carouselSlideGap(slideGap) {
  const px = cssLengthToPx(slideGap);
  return Number.isFinite(px) && px > 0 ? px : 0;
}

/**
 * Seul le défilement continu impose la boucle : AutoScroll s'arrêterait sur la
 * dernière slide sans jamais repartir. À nombre de slides fixe, le choix de
 * l'utilisateur est gardé — l'Autoplay pas-à-pas sait s'arrêter à la fin.
 */
export function carouselLoopEnabled({ loop, autoplayEnabled, maxSlidesVisible }) {
  if (autoplayEnabled && maxSlidesVisible) return true;
  return loop !== false;
}

/**
 * Options Embla du carrousel.
 * `watchDrag: false` en édition : le drag souris volait les clics de
 * sélection Puck et laissait le carrousel arrêté entre deux slides.
 */
export function carouselEmblaOptions({
  maxSlidesVisible,
  slidesVisible,
  slidesCount,
  scrollSpeed,
  loop,
  autoplayEnabled,
  isEditing,
}) {
  const visible = carouselVisibleCount(slidesVisible);
  const total = Math.max(1, Number(slidesCount) || 1);
  return {
    loop: carouselLoopEnabled({ loop, autoplayEnabled, maxSlidesVisible }),
    duration: carouselTransitionDuration(scrollSpeed),
    // Ruban : les slides s'enchaînent depuis le bord gauche, il n'y a pas de
    // slide « courante » à centrer.
    align: maxSlidesVisible || visible > 1 ? 'start' : 'center',
    slidesToScroll: maxSlidesVisible || visible === 1 ? 1 : Math.min(visible, total),
    watchDrag: !isEditing,
  };
}

/** Vitesse : un seul curseur, lente (0.5) → rapide (8), pour les deux styles. */
export const CAROUSEL_SCROLL_SPEED_MIN = 0.5;
export const CAROUSEL_SCROLL_SPEED_MAX = 8;
export const CAROUSEL_SCROLL_SPEED_STEP = 0.5;
export const CAROUSEL_SCROLL_SPEED_DEFAULT = 2;

export function carouselScrollSpeed(scrollSpeed) {
  const n = Number(scrollSpeed);
  const resolved = Number.isFinite(n) ? n : CAROUSEL_SCROLL_SPEED_DEFAULT;
  return Math.max(CAROUSEL_SCROLL_SPEED_MIN, Math.min(CAROUSEL_SCROLL_SPEED_MAX, resolved));
}

/** Bornes de la durée de transition Embla (plus élevé = plus lent). */
const TRANSITION_DURATION_SLOW = 60;
const TRANSITION_DURATION_FAST = 15;

/**
 * Le même curseur de vitesse pilote deux mécaniques différentes : des px par
 * frame pour le défilement continu (AutoScroll), une durée de transition pour
 * le pas-à-pas. La correspondance est donc inversée — vitesse haute, durée
 * basse — pour que « à droite = plus rapide » reste vrai dans les deux styles.
 * S'applique aussi au glissement manuel, quel que soit le style.
 */
export function carouselTransitionDuration(scrollSpeed) {
  const speed = carouselScrollSpeed(scrollSpeed);
  const ratio = (speed - CAROUSEL_SCROLL_SPEED_MIN)
    / (CAROUSEL_SCROLL_SPEED_MAX - CAROUSEL_SCROLL_SPEED_MIN);
  const span = TRANSITION_DURATION_SLOW - TRANSITION_DURATION_FAST;
  return Math.round(TRANSITION_DURATION_SLOW - (ratio * span));
}

/** Temps d'arrêt sur chaque slide (style `slides` uniquement), en ms. */
export const CAROUSEL_DWELL_MIN_MS = 500;
export const CAROUSEL_DWELL_MAX_MS = 15000;
export const CAROUSEL_DWELL_STEP_MS = 500;
export const CAROUSEL_DWELL_DEFAULT_MS = 4000;

export function carouselDwellMs(autoplayDelay) {
  const n = Number(autoplayDelay);
  const resolved = Number.isFinite(n) && n > 0 ? n : CAROUSEL_DWELL_DEFAULT_MS;
  return Math.max(CAROUSEL_DWELL_MIN_MS, Math.min(CAROUSEL_DWELL_MAX_MS, resolved));
}

/**
 * Options du plugin Autoplay (style `slides`) : `delay` est le temps d'arrêt
 * sur chaque slide, la durée de la transition venant d'Embla (`duration`).
 * Même contrainte que ci-dessous sur stopOnInteraction.
 */
export function carouselAutoplayOptions({ autoplayDelay, autoplayPauseOnHover = true }) {
  const pauseOnHover = autoplayPauseOnHover !== false;
  return {
    delay: carouselDwellMs(autoplayDelay),
    playOnInit: true,
    stopOnMouseEnter: pauseOnHover,
    stopOnInteraction: !pauseOnHover,
    stopOnFocusIn: true,
  };
}

/**
 * Options du plugin AutoScroll (style `continuous`).
 * stopOnInteraction doit être false quand stopOnMouseEnter est true,
 * sinon mouseleave n'est pas branché et l'autoplay ne repart pas.
 */
export function carouselAutoScrollOptions({
  scrollSpeed,
  autoplayPauseOnHover = true,
  scrollDirection = 'forward',
}) {
  const pauseOnHover = autoplayPauseOnHover !== false;
  return {
    speed: carouselScrollSpeed(scrollSpeed),
    startDelay: 0,
    playOnInit: true,
    stopOnMouseEnter: pauseOnHover,
    stopOnInteraction: !pauseOnHover,
    stopOnFocusIn: true,
    // `forward` : le ruban part vers la fin — de droite à gauche à
    // l'horizontale, de bas en haut à la verticale.
    direction: scrollDirection === 'backward' ? 'backward' : 'forward',
  };
}

/**
 * Sens du ruban, dans l'axe courant. Le plugin ne connaît que « vers la fin »
 * et « vers le début » ; ce que ça donne à l'écran dépend de l'axe, d'où deux
 * jeux de libellés pour une seule valeur stockée.
 */
export const CAROUSEL_SCROLL_DIRECTIONS = {
  horizontal: [
    { value: 'forward', label: 'De droite à gauche' },
    { value: 'backward', label: 'De gauche à droite' },
  ],
  vertical: [
    { value: 'forward', label: 'De bas en haut' },
    { value: 'backward', label: 'De haut en bas' },
  ],
};

/**
 * Orientation passée à Mantine, qui en déduit l'axe Embla. Vaut dans les deux
 * modes : à nombre fixe, les slides s'empilent et chacune prend une fraction de
 * la hauteur du cadre ; en ruban continu, elles défilent en colonne.
 */
export function carouselOrientation({ scrollAxis = 'horizontal' }) {
  return scrollAxis === 'vertical' ? 'vertical' : 'horizontal';
}

/**
 * Le défilement automatique tourne-t-il ? C'est une animation infinie
 * décorative — un bandeau de logos n'a même plus de contrôles pour l'arrêter —
 * donc la préférence système « réduire les animations » le coupe, comme elle
 * coupe déjà l'animation d'apparition d'un Bloc. Le ruban reste affiché et
 * lisible, et le glissement à la main continue de marcher.
 * Jamais d'autoplay en édition : les slides se déroberaient pendant la frappe.
 */
export function carouselAutoplayAllowed({ autoplay, isEditing = false, reducedMotion = false }) {
  return !!autoplay && !isEditing && !reducedMotion;
}

/**
 * Une zone de texte de slide s'affiche-t-elle ? En édition elle reste montée
 * même vide, sinon on ne pourrait plus jamais écrire — mais elle occupe alors
 * la hauteur de la slide, et le média (seul élément compressible du flex) tombe
 * à zéro : c'est ce qui écrasait les logos d'un bandeau. `showSlideText`
 * décoché, la slide n'est que son média, en édition comme sur le site.
 */
export function slideTextVisible({ showSlideText = true, isEditing = false, text = '' }) {
  if (!showSlideText) return false;
  return isEditing || !!text;
}
